/**
 * Pricing page showing subscription tiers and boost packs.
 * Data is fetched dynamically from Stripe via the getPricingPlans endpoint.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Elements } from "@stripe/react-stripe-js";
import { Check, Loader2, Crown, Zap, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getStripe } from "../lib/stripe";
import { formatPrice } from "../lib/pricing";
import { usePayment } from "../lib/PaymentContext";
import { getPricingPlans, type PricingPlan } from "../services/naraDoor";
import PaymentForm from "./PaymentForm";
import Header from "./Header";

type Tab = "subscriptions" | "boosts";

export default function PricingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { subscriptionPriceId, createSubscription, purchaseBoosts, hasPaymentMethod } = usePayment();
  const [activeTab, setActiveTab] = useState<Tab | null>(null);
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const {
    data: pricingData,
    isLoading: isPricingLoading,
    error: pricingError,
  } = useQuery({
    queryKey: ["pricingPlans"],
    queryFn: getPricingPlans,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const subscriptions = pricingData?.subscriptions ?? [];
  const boostPacks = pricingData?.boostPacks ?? [];

  // Only count non-free subscriptions for tab visibility
  const hasSubscriptions = subscriptions.some((s) => s.priceId !== "free");
  const hasBoosts = boostPacks.length > 0;

  // Auto-select tab based on available data
  const effectiveTab = activeTab ?? (hasSubscriptions ? "subscriptions" : hasBoosts ? "boosts" : "subscriptions");

  const findPlan = (priceId: string): PricingPlan | undefined =>
    [...subscriptions, ...boostPacks].find((p) => p.priceId === priceId);

  const selectedPlan = selectedPriceId ? findPlan(selectedPriceId) : undefined;
  const isSelectedSubscription = selectedPlan?.type === "recurring";
  const isSelectedBoost = selectedPlan?.type === "one_time";

  const handleSelectSubscription = (priceId: string) => {
    if (priceId === "free") return;

    setSelectedPriceId(priceId);
    setError(null);

    if (hasPaymentMethod) {
      setShowConfirmation(true);
    } else {
      initiateSubscription(priceId);
    }
  };

  const initiateSubscription = async (priceId: string) => {
    setIsLoading(true);
    setShowConfirmation(false);

    try {
      const result = await createSubscription(priceId);

      if (result.status === "active" && !result.clientSecret) {
        setSuccess(true);
        setSelectedPriceId(null);
      } else {
        setClientSecret(result.clientSecret);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("pricing.errors.initializeSubscription")
      );
      setSelectedPriceId(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmSubscription = () => {
    if (selectedPriceId) {
      initiateSubscription(selectedPriceId);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setSelectedPriceId(null);
  };

  const handleSelectBoostPack = async (priceId: string) => {
    setSelectedPriceId(priceId);
    setIsLoading(true);
    setError(null);

    try {
      const result = await purchaseBoosts(priceId);
      setClientSecret(result.clientSecret);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("pricing.errors.initializePurchase")
      );
      setSelectedPriceId(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccess = () => {
    setSuccess(true);
    setTimeout(() => {
      navigate("/");
    }, 2000);
  };

  const handleBack = () => {
    setSelectedPriceId(null);
    setClientSecret(null);
    setError(null);
  };

  // Selected plan details for confirmation / checkout
  const confirmPlan = selectedPriceId ? findPlan(selectedPriceId) : undefined;
  const confirmPriceDisplay = confirmPlan ? formatPrice(confirmPlan.unitAmount, confirmPlan.currency) : "";

  return (
    <div className="min-h-screen bg-muted">
      <Header />

      <div className="max-w-5xl mx-auto px-4 py-8">
        {success ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-600">
              {t("pricing.success.title")}
            </h2>
            <p className="text-muted-foreground mt-2">{t("pricing.success.redirecting")}</p>
          </div>
        ) : showConfirmation && selectedPriceId && confirmPlan ? (
          <div className="max-w-md mx-auto">
            <button
              onClick={handleCancelConfirmation}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("pricing.backToPricing")}
            </button>

            <div className="bg-card rounded-xl shadow-sm border p-6">
              <h2 className="text-xl font-semibold mb-4">{t("pricing.confirmation.title")}</h2>

              <div className="bg-muted rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{confirmPlan.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {t("pricing.confirmation.boostsPerMonth", { value: confirmPlan.boosts.toLocaleString() })}
                    </p>
                  </div>
                  <p className="text-2xl font-bold">
                    {confirmPriceDisplay}
                    <span className="text-sm font-normal text-muted-foreground">{t("pricing.confirmation.perMonth")}</span>
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  {t("pricing.confirmation.savedCardNotice")}
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-red-600 text-sm">
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleCancelConfirmation}
                  className="flex-1 py-2 px-4 border border-border text-foreground rounded-lg font-medium hover:bg-accent"
                >
                  {t("pricing.confirmation.cancel")}
                </button>
                <button
                  onClick={handleConfirmSubscription}
                  disabled={isLoading}
                  className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t("pricing.processing")}
                    </>
                  ) : (
                    t("pricing.confirmation.subscribeFor", { price: confirmPriceDisplay })
                  )}
                </button>
              </div>
            </div>
          </div>
        ) : clientSecret && selectedPriceId && selectedPlan ? (
          <div className="max-w-md mx-auto">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("pricing.backToPricing")}
            </button>

            <div className="bg-card rounded-xl shadow-sm border p-6">
              <h2 className="text-xl font-semibold mb-4">{t("pricing.checkout.title")}</h2>

              <div className="bg-muted rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{selectedPlan.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {isSelectedSubscription
                        ? t("pricing.checkout.boostsPerMonth", { value: selectedPlan.boosts.toLocaleString() })
                        : t("pricing.checkout.boosts", { value: selectedPlan.boosts.toLocaleString() })}
                    </p>
                  </div>
                  <p className="text-2xl font-bold">
                    {formatPrice(selectedPlan.unitAmount, selectedPlan.currency)}
                    {isSelectedSubscription && (
                      <span className="text-sm font-normal text-muted-foreground">
                        {t("pricing.checkout.perMonth")}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <Elements
                stripe={getStripe()}
                options={{
                  clientSecret,
                  appearance: {
                    theme: "stripe",
                    variables: {
                      colorPrimary: "#2563eb",
                    },
                  },
                }}
              >
                <PaymentForm
                  onSuccess={handleSuccess}
                  onError={setError}
                  submitLabel={
                    isSelectedSubscription
                      ? t("pricing.checkout.subscribeFor", { price: formatPrice(selectedPlan.unitAmount, selectedPlan.currency) })
                      : t("pricing.checkout.pay", { price: formatPrice(selectedPlan.unitAmount, selectedPlan.currency) })
                  }
                />
              </Elements>
            </div>
          </div>
        ) : (
          <>
            {/* Page Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold">{t("pricing.pageTitle")}</h1>
              <p className="text-muted-foreground mt-2">
                {t("pricing.pageSubtitle")}
              </p>
            </div>

            {/* Tab Switcher — only show when both types exist */}
            {hasSubscriptions && hasBoosts && (
              <div className="flex justify-center mb-8">
                <div className="bg-muted p-1 rounded-lg inline-flex">
                  <button
                    onClick={() => setActiveTab("subscriptions")}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      effectiveTab === "subscriptions"
                        ? "bg-card shadow text-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Crown className="h-4 w-4 inline mr-2" />
                    {t("pricing.tabs.subscriptions")}
                  </button>
                  <button
                    onClick={() => setActiveTab("boosts")}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      effectiveTab === "boosts"
                        ? "bg-card shadow text-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Zap className="h-4 w-4 inline mr-2" />
                    {t("pricing.tabs.boostPacks")}
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="max-w-md mx-auto mb-6 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-red-600">
                {error}
              </div>
            )}

            {pricingError && (
              <div className="max-w-md mx-auto mb-6 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-red-600">
                {t("pricing.errors.loadPricing", "Failed to load pricing plans. Please try again later.")}
              </div>
            )}

            {(isLoading || isPricingLoading) && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            )}

            {/* Subscriptions */}
            {effectiveTab === "subscriptions" && !isLoading && !isPricingLoading && (
              <div className={`grid gap-4 ${subscriptions.length <= 4 ? `md:grid-cols-${subscriptions.length}` : "md:grid-cols-4"}`} style={{ gridTemplateColumns: `repeat(${Math.min(subscriptions.length, 4)}, minmax(0, 1fr))` }}>
                {subscriptions.map((tier) => {
                  const isCurrentTier = subscriptionPriceId === tier.priceId ||
                    (tier.priceId === "free" && subscriptionPriceId === null);
                  const canUpgrade = tier.priceId !== "free" && !isCurrentTier;

                  return (
                    <div
                      key={tier.priceId}
                      className={`bg-card rounded-xl border-2 p-6 relative ${
                        tier.popular
                          ? "border-blue-500 shadow-lg"
                          : "border-border"
                      }`}
                    >
                      {tier.popular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <span className="bg-blue-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                            {t("pricing.subscription.mostPopular")}
                          </span>
                        </div>
                      )}

                      <div className="text-center mb-4">
                        <h3 className="text-lg font-semibold">{tier.name}</h3>
                        <div className="mt-2">
                          <span className="text-3xl font-bold">
                            {formatPrice(tier.unitAmount, tier.currency)}
                          </span>
                          {tier.unitAmount > 0 && (
                            <span className="text-muted-foreground">{t("pricing.subscription.perMonth")}</span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {t("pricing.subscription.boostsPerMonth", { value: tier.boosts.toLocaleString() })}
                        </p>
                      </div>

                      <ul className="space-y-2 mb-6">
                        {tier.features.map((feature, idx) => (
                          <li
                            key={idx}
                            className="flex items-start gap-2 text-sm"
                          >
                            <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {isCurrentTier ? (
                        <button
                          disabled
                          className="w-full py-2 px-4 bg-muted text-muted-foreground rounded-lg font-medium"
                        >
                          {t("pricing.subscription.currentPlan")}
                        </button>
                      ) : canUpgrade ? (
                        <button
                          onClick={() => handleSelectSubscription(tier.priceId)}
                          className={`w-full py-2 px-4 rounded-lg font-medium ${
                            tier.popular
                              ? "bg-blue-600 text-white hover:bg-blue-700"
                              : "bg-foreground text-background hover:bg-foreground/90"
                          }`}
                        >
                          {t("pricing.subscription.upgrade")}
                        </button>
                      ) : (
                        <button
                          disabled
                          className="w-full py-2 px-4 bg-muted text-muted-foreground rounded-lg font-medium"
                        >
                          {t("pricing.subscription.freeTier")}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Boost Packs */}
            {effectiveTab === "boosts" && !isLoading && !isPricingLoading && (
              <div className="max-w-2xl mx-auto">
                <p className="text-center text-muted-foreground mb-6">
                  {t("pricing.boostPacks.description")}
                </p>

                <div className={`grid gap-4`} style={{ gridTemplateColumns: `repeat(${Math.min(boostPacks.length || 1, 3)}, minmax(0, 1fr))` }}>
                  {boostPacks.map((pack) => (
                    <div
                      key={pack.priceId}
                      className={`bg-card rounded-xl border-2 p-6 ${
                        pack.popular
                          ? "border-blue-500 shadow-lg"
                          : "border-border"
                      }`}
                    >
                      {pack.popular && (
                        <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">
                          {t("pricing.boostPacks.bestValue")}
                        </span>
                      )}

                      <div className="text-center mt-2">
                        <h3 className="font-semibold">{pack.name}</h3>
                        <p className="text-3xl font-bold mt-2">
                          {formatPrice(pack.unitAmount, pack.currency)}
                        </p>
                        <p className="text-muted-foreground mt-1">
                          {t("pricing.boostPacks.boosts", { value: pack.boosts.toLocaleString() })}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t("pricing.boostPacks.perThousand", { price: `$${((pack.unitAmount / 100) / (pack.boosts / 1000)).toFixed(2)}` })}
                        </p>
                      </div>

                      <button
                        onClick={() => handleSelectBoostPack(pack.priceId)}
                        className={`w-full mt-4 py-2 px-4 rounded-lg font-medium ${
                          pack.popular
                            ? "bg-blue-600 text-white hover:bg-blue-700"
                            : "bg-foreground text-background hover:bg-foreground/90"
                        }`}
                      >
                        {t("pricing.boostPacks.buyNow")}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
